import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GridColDef } from '@mui/x-data-grid';
import { PageHeader } from '../../shared/components/PageHeader';
import { DataTable } from '../../shared/components/DataTable/DataTable';
import { ConfirmDialog } from '../../shared/components/Dialogs/ConfirmDialog';
import { StatusChip } from '../../shared/components/StatusChip';
import { resources } from '../../core/services/resources';
import { api, extractErrorMessage } from '../../core/services/api';
import { formatDate } from '../../shared/utils/format';
import type { ReportInstance } from '../../core/types/data-models';

/**
 * Standalone view of `ReportInstance` rows generated from a specific
 * report template. Extracted from the previous "Reports" tab on
 * `ReportTemplateDetail` so the sidebar's per-template navigation
 * (Settings / Prompts / Instances) can target it directly.
 *
 * The "Create report instance" button hits the same template-scoped
 * generator action as before — the backend handles the 409-on-pending
 * guard, default priority copy-down, and prompt expansion into
 * `ReportInstancePrompt` rows.
 */
export function ReportTemplateInstances() {
  const { id } = useParams();
  const templateId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const template = useQuery({
    queryKey: ['report-templates', templateId],
    queryFn: () => resources.reportTemplates.get(templateId),
    enabled: Number.isFinite(templateId),
  });

  const reportInstances = useQuery({
    queryKey: ['report-instances', { templateId }],
    queryFn: () =>
      resources.reportInstances.list({
        pageSize: 100,
        filter: { reportTemplateId: templateId },
        sort: '-id',
      }),
    enabled: Number.isFinite(templateId),
  });

  const [reportError, setReportError] = useState<string | null>(null);
  const createReportInstance = useMutation({
    mutationFn: () => api.post(`/report-templates/${templateId}/generate-instance`),
    onSuccess: () => {
      setReportError(null);
      queryClient.invalidateQueries({ queryKey: ['report-instances', { templateId }] });
    },
    onError: (err) => setReportError(extractErrorMessage(err)),
  });

  // Same server-side cascade as the global "All report instances" page:
  // deletes the instance, its generated data, and prompt responses no
  // other instance references.
  const [confirmDelete, setConfirmDelete] = useState<ReportInstance | null>(null);
  const removeInstance = useMutation({
    mutationFn: (instanceId: number) => resources.reportInstances.remove(instanceId),
    onSuccess: () => {
      setReportError(null);
      setConfirmDelete(null);
      queryClient.invalidateQueries({ queryKey: ['report-instances', { templateId }] });
    },
    onError: (err) => {
      setConfirmDelete(null);
      setReportError(extractErrorMessage(err));
    },
  });

  const columns: GridColDef<ReportInstance>[] = [
    { field: 'id', headerName: 'Report ID', width: 110 },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (p) => <StatusChip value={p.value as string} />,
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 130,
      renderCell: (p) => <StatusChip value={p.value as string} />,
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 200,
      valueFormatter: (v) => formatDate(v as string),
    },
    {
      field: '__view',
      headerName: '',
      sortable: false,
      filterable: false,
      width: 110,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (
        <>
          {/* Detail page for a ReportInstance hasn't been built yet — keep the
              affordance visible so the planned drill-in is discoverable. */}
          <IconButton size="small" aria-label="open report instance (coming soon)">
            <OpenInNewIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            disabled={removeInstance.isPending}
            onClick={() => setConfirmDelete(p.row as ReportInstance)}
            aria-label={`delete report instance ${p.row.id}`}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5" fontWeight={600}>
              {template.data?.name ?? `Report template #${templateId}`}
            </Typography>
            <Chip size="small" label="Instances" />
          </Stack>
        }
        description="Runs generated from this template. Newest first."
        actions={<Button onClick={() => navigate(-1)}>Back</Button>}
      />

      <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Report instances
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Each row is one generated run of this template.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={createReportInstance.isPending}
              onClick={() => {
                setReportError(null);
                createReportInstance.mutate();
              }}
            >
              {createReportInstance.isPending ? 'Creating…' : 'Create report instance'}
            </Button>
          </Stack>
          {reportError && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {reportError}
            </Typography>
          )}
        </Paper>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <DataTable<ReportInstance>
            rows={reportInstances.data?.data ?? []}
            columns={columns}
            loading={reportInstances.isFetching}
            paginationMode="client"
            errorMessage={
              reportInstances.isError
                ? extractErrorMessage(reportInstances.error)
                : undefined
            }
            emptyMessage="No report instances yet. Use the button above to create one."
          />
        </Box>
      </Stack>
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete report instance"
        message={`Delete report instance #${confirmDelete?.id} and all its generated data (prompts, insights, recommendations, source visibility)? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={removeInstance.isPending}
        onConfirm={() => confirmDelete && removeInstance.mutate(confirmDelete.id)}
        onClose={() => setConfirmDelete(null)}
      />
    </Box>
  );
}
